import { useEffect, useState } from "react";
import { Check, ImageUp, Moon, Sun } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { ACCENTS, applyAccent } from "@/lib/theme";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Field, PageHeader } from "@/components/common";
import { cn } from "@/lib/utils";
import { BACKEND_URL } from "@/lib/api";

const Section = ({ title, text, children, testId }) => (
  <section className="panel" data-testid={testId}>
    <h2 className="font-heading text-lg font-bold">{title}</h2>
    {text && <p className="text-sm text-muted-foreground mt-1">{text}</p>}
    <div className="mt-6">{children}</div>
  </section>
);

const Uploader = ({ kind, label, hint, url, onDone }) => {
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post(`/settings/upload/${kind}`, fd);
      toast.success(`${label} diperbarui`);
      onDone(data);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="flex items-center gap-4 rounded-2xl border p-4">
      <div className="h-20 w-20 rounded-xl bg-muted grid place-items-center overflow-hidden shrink-0 checker">
        {url ? <img src={`${BACKEND_URL}${url}`} alt={label} className="h-full w-full object-contain" data-testid={`${kind}-preview`} /> : <ImageUp className="h-7 w-7 text-muted-foreground" />}
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-semibold">{label}</p>
        <p className="text-xs text-muted-foreground">{hint}</p>
        <label className="mt-3 inline-flex h-10 items-center rounded-full border px-4 text-sm font-medium cursor-pointer hover:bg-muted" data-testid={`${kind}-upload-button`}>
          {busy ? "Mengunggah..." : "Pilih Gambar"}
          <input type="file" accept="image/png,image/jpeg,image/webp,image/x-icon,image/svg+xml" className="hidden" onChange={onFile} disabled={busy} data-testid={`${kind}-file-input`} />
        </label>
      </div>
    </div>
  );
};

export default function SettingsPage() {
  const { settings, apply, dark, toggleMode } = useSettings();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!form) setForm({ app_name: settings.app_name, pwa_name: settings.pwa_name, accent: settings.accent, dark_default: settings.dark_default, receipt_header: settings.receipt_header || "", receipt_footer: settings.receipt_footer || "" });
  }, [settings, form]);
  if (!form) return null;

  const pickAccent = (k) => { setForm((f) => ({ ...f, accent: k })); applyAccent(k); };
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings", form);
      apply(data);
      toast.success("Pengaturan disimpan");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-4xl">
      <PageHeader eyebrow="Super Admin" title="Pengaturan Aplikasi" subtitle="Identitas, tampilan, dan menu aplikasi untuk seluruh pengguna."
        action={<Button onClick={save} disabled={busy} className="rounded-full h-11 px-6" data-testid="settings-save-button">{busy ? "Menyimpan..." : "Simpan Perubahan"}</Button>} />
      <div className="space-y-6">
        <Section title="Identitas Aplikasi" text="Nama tampil di dashboard dan nama aplikasi saat di-install (PWA)." testId="settings-identity">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Nama Aplikasi (Dashboard)"><Input className="h-11" value={form.app_name} onChange={(e) => setForm({ ...form, app_name: e.target.value })} data-testid="settings-app-name-input" /></Field>
            <Field label="Nama PWA (Layar Utama HP)" hint="Maks. 30 karakter"><Input className="h-11" maxLength={30} value={form.pwa_name} onChange={(e) => setForm({ ...form, pwa_name: e.target.value })} data-testid="settings-pwa-name-input" /></Field>
          </div>
        </Section>

        <Section title="Struk Transaksi" text="Judul dan catatan kaki struk setoran & angsuran." testId="settings-receipt">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Judul Header Struk" hint="Kosong = nama aplikasi"><Input className="h-11" maxLength={120} value={form.receipt_header} onChange={(e) => setForm({ ...form, receipt_header: e.target.value })} data-testid="settings-receipt-header-input" /></Field>
            <Field label="Footer Struk"><Input className="h-11" maxLength={200} value={form.receipt_footer} onChange={(e) => setForm({ ...form, receipt_footer: e.target.value })} data-testid="settings-receipt-footer-input" /></Field>
          </div>
        </Section>

        <Section title="Logo & Favicon" text="Favicon otomatis dipakai sebagai ikon aplikasi PWA (192px & 512px). Langsung tersimpan saat diunggah." testId="settings-branding">
          <div className="grid md:grid-cols-2 gap-4">
            <Uploader kind="logo" label="Logo" hint="PNG/JPG/SVG, maks. 2MB" url={settings.logo_url} onDone={apply} />
            <Uploader kind="favicon" label="Favicon & Ikon PWA" hint="Disarankan persegi 512×512 PNG" url={settings.favicon_url} onDone={apply} />
          </div>
        </Section>

        <Section title="Tema" text="Aksen warna berlaku untuk semua pengguna. Mode gelap bawaan dipakai jika pengguna belum memilih sendiri lewat tombol bulan/matahari." testId="settings-theme">
          <p className="eyebrow mb-3">Aksen Warna</p>
          <div className="flex flex-wrap gap-3">
            {Object.entries(ACCENTS).map(([k, a]) => (
              <button key={k} type="button" onClick={() => pickAccent(k)} data-testid={`accent-color-${k}`}
                className={cn("flex items-center gap-2.5 rounded-full border pl-1.5 pr-4 py-1.5 text-sm font-medium transition-colors min-h-[44px]", form.accent === k ? "border-foreground" : "hover:bg-muted")}>
                <span className="h-8 w-8 rounded-full grid place-items-center" style={{ background: a.hex }}>{form.accent === k && <Check className="h-4 w-4 text-white" />}</span>
                {a.label}
              </button>
            ))}
          </div>
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            <div className="flex items-center justify-between rounded-2xl border p-4">
              <div className="flex items-center gap-3"><Moon className="h-5 w-5" /><div><p className="font-semibold text-sm">Mode Gelap Bawaan</p><p className="text-xs text-muted-foreground">Untuk semua pengguna</p></div></div>
              <Switch checked={form.dark_default} onCheckedChange={(v) => setForm({ ...form, dark_default: v })} data-testid="settings-dark-default-switch" />
            </div>
            <div className="flex items-center justify-between rounded-2xl border p-4">
              <div className="flex items-center gap-3">{dark ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}<div><p className="font-semibold text-sm">Tampilan Saya</p><p className="text-xs text-muted-foreground">{dark ? "Gelap" : "Terang"} (hanya perangkat ini)</p></div></div>
              <Switch checked={dark} onCheckedChange={toggleMode} data-testid="settings-my-dark-switch" />
            </div>
          </div>
        </Section>
      </div>
    </div>
  );
}
